import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import {
  LayoutDashboard,
  ShoppingCart,
  MessageSquare,
  User,
  Bell,
  LogOut,
  ChevronRight,
  ChevronDown,
  Menu,
  X,
  Truck,
  Package,
  CheckCircle2,
  Clock,
  AlertCircle,
  MapPin,
  Phone,
  Mail,
  Building2,
  Star,
  Send,
  Edit3,
  Camera,
  Shield,
  Key,
  Download,
  Eye,
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "./user.css";

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "orders", label: "Riwayat Order", icon: ShoppingCart },
  { id: "suggestion", label: "Kotak Saran", icon: MessageSquare },
  { id: "profile", label: "Profil Saya", icon: User },
];

const ORDERS = [
  {
    id: "SBB-2025-0241",
    date: "2 Mei 2025",
    product: "Beton K300 – 14 m³",
    status: "dispatched",
    total: "Rp 14.700.000",
    plant: "Plant Cilincing",
    eta: "~22 mnt",
  },
  {
    id: "SBB-2025-0220",
    date: "18 Apr 2025",
    product: "MiniMix K250 – 3 m³",
    status: "completed",
    total: "Rp 3.900.000",
    plant: "Plant Pondok Indah",
    eta: null,
  },
  {
    id: "SBB-2025-0198",
    date: "5 Apr 2025",
    product: "Beton K350 – 21 m³",
    status: "completed",
    total: "Rp 23.100.000",
    plant: "Plant Kalibaru",
    eta: null,
  },
  {
    id: "SBB-2025-0172",
    date: "20 Mar 2025",
    product: "Beton K225 – 7 m³",
    status: "completed",
    total: "Rp 5.950.000",
    plant: "Plant Cilincing",
    eta: null,
  },
  {
    id: "SBB-2025-0150",
    date: "8 Mar 2025",
    product: "Beton K400 – 30 m³",
    status: "invoiced",
    total: "Rp 37.500.000",
    plant: "Plant Kalibaru",
    eta: null,
  },
];
const STATUS_MAP = {
  dispatched: { label: 'portal.status.dispatched', cls: "up-dispatched", icon: Truck },
  completed: { label: 'portal.status.completed', cls: "up-completed", icon: CheckCircle2 },
  invoiced: { label: 'portal.status.invoiced', cls: "up-invoiced", icon: AlertCircle },
  batching: { label: 'portal.status.batching', cls: "up-batching", icon: Package },
  approved: { label: 'portal.status.approved', cls: "up-approved", icon: CheckCircle2 },
};

export default function UserPanel() {
  const { user, logout, loading: authLoading } = useAuth();
  const { t, lang } = useLanguage();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Wait until AuthContext finishes reading localStorage
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchOrders();
  }, [user, authLoading]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('sbb_token');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Failed to fetch orders");
      const data = await response.json();
      setOrders(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderDetail = async (orderId) => {
    try {
      const token = localStorage.getItem('sbb_token');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/orders/${orderId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Failed to fetch order details");
      const data = await response.json();
      setSelectedOrder(data);
    } catch (err) {
      console.error(err);
    }
  };

  const NAV_TABS = [
    { id: "dashboard", label: t('portal.dashboard'), icon: LayoutDashboard },
    { id: "orders", label: t('portal.orders'), icon: ShoppingCart },
    { id: "suggestion", label: t('portal.suggestion.title'), icon: MessageSquare },
    { id: "profile", label: t('portal.profile.title'), icon: User },
  ];

  return (
    <div className="up-root">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="up-overlay" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar (desktop only) */}
      <aside className={`up-sidebar ${mobileOpen ? "open" : ""}`}>
        <div className="up-sb-top">
          <Link to="/" className="up-sb-brand">
            <div className="up-sb-mark">SB</div>
            <div>
              <div className="up-sb-name">Solusi Bangun Beton</div>
              <div className="up-sb-sub">{t('portal.sub')}</div>
            </div>
          </Link>
          <button className="up-sb-close" onClick={() => setMobileOpen(false)}>
            <X size={18} />
          </button>
        </div>

        {/* User info card */}
        <div className="up-user-card">
          <div className="up-user-avatar">{user?.name?.substring(0, 2).toUpperCase() || 'U'}</div>
          <div>
            <div className="up-user-name">{user?.name || 'User'}</div>
            <div className="up-user-type">{lang === 'id' ? 'Pelanggan Reguler' : 'Regular Customer'}</div>
          </div>
          <div className="up-user-stars">
            <Star size={12} fill="#8DC63F" color="#8DC63F" />
            <span>4.8</span>
          </div>
        </div>

        <nav className="up-nav">
          {NAV_TABS.map((t_tab) => (
            <button
              key={t_tab.id}
              className={`up-nav-item ${activeTab === t_tab.id ? "active" : ""}`}
              onClick={() => {
                setActiveTab(t_tab.id);
                setSelectedOrder(null);
                setMobileOpen(false);
              }}
            >
              <t_tab.icon size={18} />
              <span>{t_tab.label}</span>
              {activeTab === t_tab.id && (
                <ChevronRight size={14} className="up-nav-arrow" />
              )}
            </button>
          ))}
        </nav>

        <div className="up-sb-footer">
          <Link to="/" className="up-sb-back">
            <ChevronRight size={14} style={{ transform: "rotate(180deg)" }} />
            {t('portal.backToWeb')}
          </Link>
          <button className="up-nav-item logout" onClick={logout}>
            <LogOut size={20} />
            <span>{t('portal.logout')}</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="up-main">
        {/* Topbar */}
        <header className="up-topbar">
          <div className="up-topbar-left">
            <button className="up-ham" onClick={() => setMobileOpen(true)}>
              <Menu size={20} />
            </button>
            <div>
              <div className="up-topbar-title">
                {activeTab === 'dashboard' ? t('portal.dashboard')
                  : activeTab === 'orders' ? t('portal.orders')
                  : activeTab === 'suggestion' ? t('portal.suggestion.title')
                  : t('portal.profile.title')}
              </div>
              <div className="up-topbar-sub">
                {t('portal.welcome')}, {user?.name || 'User'} 👋
              </div>
            </div>
          </div>
          <div className="up-topbar-right">
            <button className="up-notif-btn">
              <Bell size={18} />
              <span className="up-notif-badge">2</span>
            </button>
            <Link
              to="/products"
              className="btn btn-red"
              style={{ padding: "8px 18px", fontSize: "13px" }}
            >
              + {t('portal.orderBtn')}
            </Link>
          </div>
        </header>

        {/* Content */}
        <div className="up-content">
          {loading ? (
            <div className="loading-state">{t('portal.loading')}</div>
          ) : (
            <>
              {activeTab === "dashboard" && !selectedOrder && (
                <DashboardTab
                  orders={orders}
                  onViewOrder={(o) => {
                    fetchOrderDetail(o.id);
                    setActiveTab("orders");
                  }}
                />
              )}
              {activeTab === "orders" && !selectedOrder && (
                <OrdersTab
                  orders={orders}
                  onView={(o) => fetchOrderDetail(o.id)}
                />
              )}
              {selectedOrder && (
                <OrderDetail
                  order={selectedOrder}
                  onBack={() => setSelectedOrder(null)}
                />
              )}
              {activeTab === "suggestion" && <SuggestionTab />}
              {activeTab === "profile" && <ProfileTab />}
            </>
          )}
        </div>
      </div>

      {/* ── Mobile Bottom Navigation Bar ───────────────────── */}
      <nav className="up-bottom-nav">
        {NAV_TABS.map((tab) => (
          <button
            key={tab.id}
            className={`up-bn-item ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => {
              setActiveTab(tab.id);
              setSelectedOrder(null);
            }}
          >
            <div className="up-bn-icon">
              <tab.icon size={20} />
            </div>
            <span>{tab.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

/* ── Dashboard Tab ─────────────────────────────────────────────────────── */
function DashboardTab({ orders, onViewOrder }) {
  const { t } = useLanguage();
  const active = orders.find((o) => o.status === "dispatched");
  return (
    <div className="up-dashboard">
      {/* Active order alert */}
      {active && (
        <div className="up-active-order">
          <div className="up-ao-pulse">
            <Truck size={20} color="white" />
          </div>
          <div className="up-ao-info">
            <div className="up-ao-title">{t('portal.activeOrder')}</div>
            <div className="up-ao-sub">
              Order <strong>{active.order_number}</strong> · {active.project_name} · ETA{" "}
              {active.eta || '15 mnt'}
            </div>
          </div>
          <button className="up-ao-btn" onClick={() => onViewOrder(active)}>
            {t('portal.monitorNow')} <ChevronRight size={14} />
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="up-dash-stats">
        {[
          {
            label: t('portal.stats.orders'),
            value: orders.length,
            icon: ShoppingCart,
            color: "green",
          },
          {
            label: t('portal.stats.volume'),
            value: "75 m³",
            icon: Package,
            color: "blue",
          },
          {
            label: t('portal.stats.spend'),
            value: "Rp 85,1jt",
            icon: Building2,
            color: "purple",
          },
          { label: t('portal.stats.rating'), value: "4.8 ★", icon: Star, color: "orange" },
        ].map((s, i) => (
          <div key={i} className={`up-dash-stat up-dash-${s.color}`}>
            <div className="up-ds-icon">
              <s.icon size={20} />
            </div>
            <div className="up-ds-val">{s.value}</div>
            <div className="up-ds-lbl">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Recent orders */}
      <div className="up-section-head">
        <h2>{t('portal.recentOrders')}</h2>
        <button className="up-see-all" onClick={() => {}}>
          {t('portal.seeAll')} →
        </button>
      </div>
      <div className="up-order-list">
        {orders.slice(0, 3).map((o) => (
          <OrderCard key={o.id} order={o} onView={() => onViewOrder(o)} />
        ))}
      </div>
    </div>
  );
}

/* ── Orders Tab ────────────────────────────────────────────────────────── */
function OrdersTab({ orders, onView }) {
  const { t } = useLanguage();
  const [filter, setFilter] = useState("all");
  const filtered =
    filter === "all" ? orders : orders.filter((o) => o.status === filter);
  return (
    <div>
      <div className="up-filter-row">
        {["all", "dispatched", "completed", "invoiced"].map((f) => (
          <button
            key={f}
            className={`up-filter-btn ${filter === f ? "active" : ""}`}
            onClick={() => setFilter(f)}
          >
            {f === "all" ? t('portal.filterAll') : t(STATUS_MAP[f]?.label)}
          </button>
        ))}
      </div>
      <div className="up-order-list">
        {filtered.map((o) => (
          <OrderCard key={o.id} order={o} onView={() => onView(o)} />
        ))}
      </div>
    </div>
  );
}

function OrderCard({ order, onView }) {
  const { t, lang } = useLanguage();
  const st = STATUS_MAP[order.status] || STATUS_MAP.completed;
  const dateStr = new Date(order.created_at).toLocaleDateString(lang === 'id' ? "id-ID" : "en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return (
    <div className="up-order-card" onClick={onView}>
      <div className="up-oc-left">
        <div className={`up-oc-status-icon ${order.status}`}>
          <st.icon size={16} />
        </div>
        <div>
          <div className="up-oc-id">{order.order_number}</div>
          <div className="up-oc-product">{order.project_name}</div>
          <div className="up-oc-meta">
            <MapPin size={11} /> {order.plant_name || "Plant TBD"} · {dateStr}
          </div>
        </div>
      </div>
      <div className="up-oc-right">
        <div className="up-oc-total">
          Rp {parseInt(order.estimated_total).toLocaleString(lang === 'id' ? "id-ID" : "en-US")}
        </div>
        <span className={`up-oc-badge ${st.cls}`}>{t(st.label)}</span>
        <button className="up-oc-view">
          {lang === 'id' ? 'Detail' : 'Details'} <Eye size={13} />
        </button>
      </div>
    </div>
  );
}

/* ── Live Tracking Component (Real Map via Leaflet) ───────────────────── */
function LiveTracking({ order }) {
  const { t } = useLanguage();
  const [progress, setProgress] = useState(0); // 0 to 1 ratio

  // Koordinat Demo: Plant Cilincing ke Lokasi Proyek di Kelapa Gading / Sunter
  const plantCoord = [-6.1285, 106.9385]; // Cilincing
  const projectCoord = [-6.1550, 106.8900]; // Kelapa Gading

  // Hitung posisi terkini truk berdasarkan interpolasi linear
  const currentLat = plantCoord[0] + (projectCoord[0] - plantCoord[0]) * progress;
  const currentLng = plantCoord[1] + (projectCoord[1] - plantCoord[1]) * progress;

  // Efek pergerakan truk real-time
  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 0.95) return 0; // Ulang dari awal untuk keperluan demo terus-menerus
        return prev + 0.01; // Maju 1% setiap detik
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Kustom Icon menggunakan L.divIcon agar mendukung styling warna SBB dan ikon CSS
  const plantIcon = L.divIcon({
    className: "custom-leaflet-icon",
    html: `<div style="background-color: var(--dark); color: white; width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3);">P</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });

  const projectIcon = L.divIcon({
    className: "custom-leaflet-icon",
    html: `<div style="background-color: var(--red); color: white; width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3);">D</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });

  const truckIcon = L.divIcon({
    className: "custom-leaflet-icon truck-animated",
    html: `<div style="background-color: var(--red); color: white; padding: 4px 8px; border-radius: 6px; font-size: 11px; font-weight: bold; display: flex; align-items: center; gap: 4px; white-space: nowrap; border: 2px solid white; box-shadow: 0 4px 10px rgba(141,198,63,0.4);">
      🚚 Truk #B-9124
    </div>`,
    iconSize: [100, 28],
    iconAnchor: [50, 14],
  });

  return (
    <div className="up-card up-live-tracking">
      <div className="up-lt-head">
        <div>
          <div className="up-lt-title">{t('portal.tracking.title')}</div>
          <div className="up-lt-sub">
            Truk #B-9124-SBB · {t('portal.tracking.driver')}: Ahmad Rizal
          </div>
        </div>
        <div className="up-lt-eta">{t('portal.tracking.eta')}: {order.eta || "15 mnt"}</div>
      </div>

      {/* Kontainer Peta Leaflet Nyata */}
      <div className="up-lt-realmap-wrapper" style={{ height: "300px", width: "100%", borderRadius: "12px", overflow: "hidden", position: "relative", zIndex: 1, border: "1px solid var(--border)" }}>
        <MapContainer
          center={[-6.1417, 106.9142]} // Titik tengah antara Cilincing dan Kelapa Gading
          zoom={13}
          scrollWheelZoom={false}
          style={{ height: "100%", width: "100%" }}
        >
          {/* Menggunakan ubin peta jalan raya dari OpenStreetMap yang rapi */}
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* Marker Asal (Plant) */}
          <Marker position={plantCoord} icon={plantIcon}>
            <Popup>
              <strong>Plant SBB Cilincing</strong><br />Titik keberangkatan truk mixer.
            </Popup>
          </Marker>

          {/* Marker Tujuan (Lokasi Proyek) */}
          <Marker position={projectCoord} icon={projectIcon}>
            <Popup>
              <strong>{order.project_name || "Lokasi Proyek"}</strong><br />{order.project_address || "Tujuan Pengecoran"}
            </Popup>
          </Marker>

          {/* Marker Kendaraan Bergerak (Truk) */}
          <Marker position={[currentLat, currentLng]} icon={truckIcon}>
            <Popup>
              <strong>Truk Mixer Sedang Berjalan</strong><br />Kecepatan: ~40 km/jam.
            </Popup>
          </Marker>
        </MapContainer>
      </div>

      <div className="up-lt-status" style={{ marginTop: "16px" }}>
        <div className="up-lt-st-item done">
          <div className="up-lt-st-dot done" />
          <span>{t('portal.tracking.start')} (09:45)</span>
        </div>
        <div className="up-lt-st-item active">
          <div className="up-lt-st-dot pulse" />
          <span>{t('portal.tracking.onWay')}</span>
        </div>
        <div className="up-lt-st-item">
          <div className="up-lt-st-dot" />
          <span>{t('portal.tracking.arrived')}</span>
        </div>
      </div>
    </div>
  );
}

/* ── Order Detail Tab ──────────────────────────────────────────────────── */
function OrderDetail({ order, onBack }) {
  const { t, lang } = useLanguage();
  const st = STATUS_MAP[order.status] || STATUS_MAP.completed;
  const dateStr = new Date(order.created_at).toLocaleDateString(lang === 'id' ? "id-ID" : "en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const steps = [
    { label: "Permintaan Dikirim", done: true, time: "08:30" },
    {
      label: "Sales Review",
      done: order.status !== "submitted",
      time: "08:45",
    },
    {
      label: "Quotation Dikirim",
      done: !["submitted", "sales_review"].includes(order.status),
      time: "09:15",
    },
    {
      label: "Truk Berangkat",
      done: ["dispatched", "completed"].includes(order.status),
      time: "09:45",
    },
    {
      label: "Selesai",
      done: order.status === "completed",
      time: order.status === "completed" ? "12:30" : "--:--",
    },
  ];
  return (
    <div className="up-order-detail">
      <button className="up-back-btn" onClick={onBack}>
        ← {t('portal.detail.back')}
      </button>

      <div className="up-od-header">
        <div>
          <div className="up-od-id">{order.order_number}</div>
          <div className="up-od-date">
            {dateStr} · {order.plant_name || "Plant TBD"}
          </div>
        </div>
        <span className={`up-oc-badge ${st.cls}`}>{t(st.label)}</span>
      </div>

      <div className="up-od-grid">
        {/* Timeline & Project Info */}
        <div className="up-od-left-col">
          {/* Panel Live Tracking Dummy (Selalu Tampil untuk Demo) */}
          <LiveTracking order={order} />

          {/* Project Info Section */}
          <div className="up-card">
            <h3 className="up-card-title">{t('portal.detail.projectInfo')}</h3>
            <div className="up-project-info-grid">
              <div className="up-pi-item">
                <div className="up-pi-lbl">{t('portal.detail.customerName')}</div>
                <div className="up-pi-val">{order.customer_name}</div>
              </div>
              <div className="up-pi-item">
                <div className="up-pi-lbl">{t('portal.detail.phone')}</div>
                <div className="up-pi-val">{order.phone}</div>
              </div>
              <div className="up-pi-item">
                <div className="up-pi-lbl">{t('portal.detail.email')}</div>
                <div className="up-pi-val">{order.email}</div>
              </div>
              <div className="up-pi-item full">
                <div className="up-pi-lbl">{t('portal.detail.address')}</div>
                <div className="up-pi-val">{order.project_address}</div>
              </div>
            </div>
          </div>

          {/* Order Items Section */}
          <div className="up-card">
            <h3 className="up-card-title">{t('portal.detail.items')}</h3>
            <div className="up-items-table-wrap">
              <table className="up-items-table">
                <thead>
                  <tr>
                    <th>{t('portal.detail.itemProduct')}</th>
                    <th>{t('portal.detail.itemQty')}</th>
                    <th>{t('portal.detail.itemPrice')}</th>
                    <th className="text-right">{t('portal.detail.itemSub')}</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items?.map((item, idx) => (
                    <tr key={idx}>
                      <td>
                        <div className="up-it-name">{item.product_name}</div>
                        <div className="up-it-cat">{item.product_category} · {item.truck_type}</div>
                      </td>
                      <td>{item.volume_m3} m³</td>
                      <td>Rp {parseInt(item.unit_price).toLocaleString(lang === 'id' ? "id-ID" : "en-US")}</td>
                      <td className="text-right">Rp {parseInt(item.subtotal).toLocaleString(lang === 'id' ? "id-ID" : "en-US")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="up-card">
            <h3 className="up-card-title">{t('portal.detail.shippingStatus')}</h3>
            {order.status === "dispatched" && (
              <div className="up-tracking-bar">
                <Truck size={16} /> {t('portal.tracking.onWay')} · ETA {order.eta || '15 mnt'}
              </div>
            )}
            <div className="up-timeline">
              {steps.map((s, i) => (
                <div key={i} className={`up-tl-item ${s.done ? "done" : ""}`}>
                  <div className="up-tl-dot">
                    {s.done && <CheckCircle2 size={12} />}
                  </div>
                  <div className="up-tl-line" />
                  <div className="up-tl-body">
                    <div className="up-tl-label">{s.label}</div>
                    <div className="up-tl-time">{s.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="up-od-aside">
          {/* Order summary */}
          <div className="up-card">
            <h3 className="up-card-title">{t('portal.detail.summary')}</h3>
            <div className="up-detail-rows">
              <div className="up-dr">
                <span>{t('portal.detail.project')}</span>
                <strong>{order.project_name}</strong>
              </div>
              <div className="up-dr">
                <span>{t('portal.detail.plant')}</span>
                <strong>{order.plant_name || "TBD"}</strong>
              </div>
              <div className="up-dr">
                <span>{t('portal.detail.date')}</span>
                <strong>{dateStr}</strong>
              </div>
              <div className="up-dr">
                <span>{t('portal.detail.payStatus')}</span>
                <strong style={{ color: "#22c55e" }}>{t('portal.detail.paid')}</strong>
              </div>
            </div>
            <div className="up-total-box">
              <span>{t('portal.detail.total')}</span>
              <strong>
                Rp {parseInt(order.estimated_total).toLocaleString(lang === 'id' ? "id-ID" : "en-US")}
              </strong>
            </div>
          </div>

          {/* Actions */}
          <div className="up-card up-card-actions-card">
            <button className="up-action-btn green">
              <Download size={15} /> {t('portal.detail.download')}
            </button>
            <button className="up-action-btn outline">
              <MessageSquare size={15} /> {t('portal.detail.contactCS')}
            </button>
            {order.status === "completed" && (
              <button className="up-action-btn ghost">
                <Star size={15} /> {t('portal.detail.review')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Suggestion Tab ────────────────────────────────────────────────────── */
function SuggestionTab() {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    category: "",
    message: "",
    rating: 0,
    anon: false,
  });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
  };

  if (sent) {
    return (
      <div className="up-sent-wrap">
        <div className="up-sent-icon">
          <CheckCircle2 size={48} color="#8DC63F" />
        </div>
        <h2>{t('portal.suggestion.successTitle')}</h2>
        <p>{t('portal.suggestion.successDesc')}</p>
        <button className="up-action-btn green" onClick={() => setSent(false)}>
          {t('portal.suggestion.sendOther')}
        </button>
      </div>
    );
  }

  return (
    <div className="up-suggestion">
      <div className="up-sug-hero">
        <MessageSquare size={32} color="#8DC63F" />
        <div>
          <h2>{t('portal.suggestion.title')}</h2>
          <p>{t('portal.suggestion.desc')}</p>
        </div>
      </div>

      <div className="up-sug-grid">
        <form className="up-card up-sug-form" onSubmit={handleSubmit}>
          <div className="up-fg">
            <label>{t('portal.suggestion.category')}</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              required
            >
              <option value="">{t('portal.suggestion.selectCat')}</option>
              <option>Kualitas Produk Beton</option>
              <option>Ketepatan Waktu Pengiriman</option>
              <option>Pelayanan Customer Service</option>
              <option>Kemudahan Platform Digital</option>
              <option>Harga & Transparansi</option>
              <option>Lainnya</option>
            </select>
          </div>

          <div className="up-fg">
            <label>{t('portal.suggestion.rating')}</label>
            <div className="up-rating-row">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  type="button"
                  key={n}
                  className={`up-star-btn ${form.rating >= n ? "lit" : ""}`}
                  onClick={() => setForm({ ...form, rating: n })}
                >
                  <Star
                    size={28}
                    fill={form.rating >= n ? "#8DC63F" : "none"}
                    color={form.rating >= n ? "#8DC63F" : "#ccc"}
                  />
                </button>
              ))}
              {form.rating > 0 && (
                <span className="up-rating-label">
                  {
                    ["", "Buruk", "Kurang", "Cukup", "Bagus", "Sangat Bagus"][
                      form.rating
                    ]
                  }
                </span>
              )}
            </div>
          </div>

          <div className="up-fg">
            <label>{t('portal.suggestion.message')}</label>
            <textarea
              rows={6}
              placeholder={t('portal.suggestion.messagePlaceholder')}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              required
            />
          </div>

          <label className="up-anon-check">
            <input
              type="checkbox"
              checked={form.anon}
              onChange={(e) => setForm({ ...form, anon: e.target.checked })}
            />
            <span>{t('portal.suggestion.anon')}</span>
          </label>

          <button
            type="submit"
            className="up-action-btn green"
            style={{ width: "100%", justifyContent: "center" }}
          >
            <Send size={16} /> {t('portal.suggestion.submit')}
          </button>
        </form>

        <div className="up-sug-aside">
          <div className="up-card">
            <h3 className="up-card-title">Kenapa Saran Anda Penting?</h3>
            {[
              {
                icon: "🎯",
                title: "Langsung Ditindaklanjuti",
                desc: "Tim kami membalas setiap saran dalam 2×24 jam kerja.",
              },
              {
                icon: "🔒",
                title: "Privasi Terjaga",
                desc: "Opsi anonim tersedia agar Anda bebas berpendapat.",
              },
              {
                icon: "📈",
                title: "Berdampak Nyata",
                desc: "Saran pelanggan menjadi prioritas peningkatan layanan.",
              },
            ].map((i, idx) => (
              <div key={idx} className="up-info-item">
                <div className="up-info-icon">{i.icon}</div>
                <div>
                  <div className="up-info-title">{i.title}</div>
                  <div className="up-info-desc">{i.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="up-card up-contact-card">
            <h3 className="up-card-title">Butuh Bantuan Segera?</h3>
            <a
              href="https://wa.me/6281234567890"
              className="up-action-btn green"
              style={{ display: "flex", justifyContent: "center" }}
            >
              <Phone size={15} /> WhatsApp CS
            </a>
            <a
              href="mailto:cs@sbb.co.id"
              className="up-action-btn outline"
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "10px",
              }}
            >
              <Mail size={15} /> Email Kami
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Profile Tab ───────────────────────────────────────────────────────── */
function ProfileTab() {
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "User",
    phone: user?.phone || "-",
    email: user?.email || "-",
    company: "CV Bangun Jaya",
    address: "Jl. Sudirman No. 12, Jakarta Selatan",
    type: lang === 'id' ? "Kontraktor" : "Contractor",
  });

  return (
    <div className="up-profile">
      {/* Header banner */}
      <div className="up-profile-banner">
        <div className="up-prof-av-wrap">
          <div className="up-prof-av">BW</div>
          <button className="up-prof-av-edit">
            <Camera size={14} />
          </button>
        </div>
        <div className="up-prof-info">
          <h2>{form.name}</h2>
          <div className="up-prof-meta">
            <span className="up-prof-badge">{form.type}</span>
            <span>
              <Mail size={12} /> {form.email}
            </span>
            <span>
              <Phone size={12} /> {form.phone}
            </span>
          </div>
        </div>
        <button
          className="up-prof-edit-btn"
          onClick={() => setEditMode(!editMode)}
        >
          <Edit3 size={15} /> {editMode ? t('portal.profile.cancel') : t('portal.profile.edit')}
        </button>
      </div>

      <div className="up-profile-grid">
        {/* Personal info */}
        <div className="up-card">
          <h3 className="up-card-title">{t('portal.profile.personalData')}</h3>
          <div className="up-form-grid">
            {[
              { label: t('portal.profile.fullName'), key: "name", icon: User },
              { label: t('portal.profile.phone'), key: "phone", icon: Phone },
              { label: t('portal.profile.email'), key: "email", icon: Mail },
              { label: t('portal.profile.company'), key: "company", icon: Building2 },
              { label: t('portal.profile.custType'), key: "type", icon: Shield },
            ].map((f) => (
              <div key={f.key} className="up-prof-field">
                <label>
                  <f.icon size={13} /> {f.label}
                </label>
                {editMode ? (
                  <input
                    value={form[f.key]}
                    onChange={(e) =>
                      setForm({ ...form, [f.key]: e.target.value })
                    }
                  />
                ) : (
                  <div className="up-prof-value">{form[f.key]}</div>
                )}
              </div>
            ))}
            <div className="up-prof-field up-prof-field-full">
              <label>
                <MapPin size={13} /> {t('portal.profile.address')}
              </label>
              {editMode ? (
                <textarea
                  rows={2}
                  value={form.address}
                  onChange={(e) =>
                    setForm({ ...form, address: e.target.value })
                  }
                />
              ) : (
                <div className="up-prof-value">{form.address}</div>
              )}
            </div>
          </div>
          {editMode && (
            <button
              className="up-action-btn green"
              style={{
                marginTop: "20px",
                width: "100%",
                justifyContent: "center",
              }}
            >
              {t('portal.profile.save')}
            </button>
          )}
        </div>

        {/* Right column */}
        <div className="up-prof-aside">
          {/* Account security */}
          <div className="up-card">
            <h3 className="up-card-title">{t('portal.profile.security')}</h3>
            <div className="up-security-items">
              {[
                {
                  icon: Key,
                  label: t('portal.profile.changePass'),
                  sub: lang === 'id' ? "Terakhir diubah 30 hari lalu" : "Last changed 30 days ago",
                  color: "blue",
                },
                {
                  icon: Phone,
                  label: t('portal.profile.verifyWA'),
                  sub: lang === 'id' ? "Nomor telah terverifikasi ✓" : "Number verified ✓",
                  color: "green",
                },
                {
                  icon: Shield,
                  label: t('portal.profile.twoFactor'),
                  sub: lang === 'id' ? "Belum diaktifkan" : "Not activated",
                  color: "orange",
                },
              ].map((s, i) => (
                <div key={i} className={`up-sec-item up-sec-${s.color}`}>
                  <div className="up-sec-icon">
                    <s.icon size={18} />
                  </div>
                  <div>
                    <div className="up-sec-label">{s.label}</div>
                    <div className="up-sec-sub">{s.sub}</div>
                  </div>
                  <ChevronRight size={16} className="up-sec-arrow" />
                </div>
              ))}
            </div>
          </div>

          {/* Loyalty card */}
          <div className="up-loyalty-card">
            <div className="up-loyalty-top">
              <div>
                <div className="up-loyalty-tier">{t('portal.profile.loyaltyTier')}</div>
                <div className="up-loyalty-pts">850 {t('portal.profile.points')}</div>
              </div>
              <Star size={32} fill="rgba(255,255,255,0.3)" color="white" />
            </div>
            <div className="up-loyalty-bar-wrap">
              <div className="up-loyalty-bar-label">
                <span>850 / 1.000 {t('portal.profile.points')} {lang === 'id' ? 'ke Tier Platinum' : 'to Platinum Tier'}</span>
                <span>85%</span>
              </div>
              <div className="up-loyalty-bar">
                <div className="up-loyalty-fill" style={{ width: "85%" }} />
              </div>
            </div>
            <div className="up-loyalty-perks">
              <span>✓ Diskon 5%</span>
              <span>✓ Prioritas Booking</span>
              <span>✓ CS Dedicated</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
